import { Component, EventEmitter, h, Event } from '@stencil/core';
import { createFocusTrap } from 'focus-trap';
import appStore from '@stores/app.store';
import { WidgetPosition } from '@entities/WidgetPosition';
import { Appearance } from '@entities/Appearance';
import { AppRouter } from '@entities/AppRouter';
import { Configuration } from '@entities/Configuration';
import { ServiceType } from '@entities/ServiceType';
import { OngoingTransactionalService } from '@services/OngoingTransactionalService';
@Component({
  tag: 'wog-features-form',
})
export class FeaturesForm {
  @Event() featuresFormClose: EventEmitter<void>;

  private readonly ongoingServices = new OngoingTransactionalService();

  private feedbackContainer: HTMLDivElement;
  private trap = createFocusTrap('', {});

  componentDidLoad() {
    this.trap = createFocusTrap(this.feedbackContainer, {
      allowOutsideClick: true,
      clickOutsideDeactivates: true,
      escapeDeactivates: false,
      initialFocus: this.feedbackContainer,
      fallbackFocus: this.feedbackContainer,
      onPostActivate: () => {
        this.trapDeactivate();
        document.body.focus();
      },
    });
    this.trapActivate();
  }

  private trapActivate = () => {
    if (!this.trap.active) {
      this.trap.activate();
    }
  };

  private trapDeactivate = () => {
    if (this.trap.active) {
      this.trap.deactivate();
    }
  };

  private handleKeyEnter = e => {
    if (e.key === 'Enter' || e.key === ' ') {
      //enter or spacebar
      this.handleToBrt();
    }
  };

  private handleCancel = () => {
    this.featuresFormClose.emit();
  };

  private handleToBrt = () => {
    appStore.set('route', AppRouter.BugReportFormRoute);
  };

  private brt = (configuration: Configuration) => {
    const serviceType = appStore.get('serviceType');
    const { cardLabel, cardDescription } =
      serviceType === ServiceType.InformationalService
        ? configuration.bugReportTool.informationalService
        : configuration.bugReportTool.transactionalServices.filter(
            e => e.trackingId === this.ongoingServices.getActiveTrackingId(),
          )[0];
    return (
      <div
        class={'wog--feature-style wog--flex wog--px-2'}
        aria-label="report a problem"
        tabIndex={0}
        role="group"
        aria-roledescription="Sentiments Bug Report"
        aria-labelledby="sentiments bug report"
        onClick={this.handleToBrt}
        onKeyDown={this.handleKeyEnter}>
        <div class={'wog--flex wog--items-center wog--justify-center wog--px-2'}>
          <svg width="24" height="24" viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg">
            {/* <!-- Antenna LEFT --> */}
            <rect
              x="25"
              y="1"
              width="6"
              height="16"
              rx="3"
              transform="rotate(-45 32 13)"
              fill="#D53F52"
            />

            {/* <!-- Antenna RIGHT (mirror of left) --> */}
            <rect
              x="35"
              y="1"
              width="6"
              height="16"
              rx="3"
              transform="rotate(45 32 13)"
              fill="#D53F52"
            />

            {/* <!-- Body --> */}
            <rect x="16" y="12" width="32" height="40" rx="14" fill="#D53F52" />

            {/* <!-- Left legs --> */}
            <rect x="8" y="22" width="10" height="6" rx="3" fill="#D53F52" />
            <rect x="8" y="30" width="10" height="6" rx="3" fill="#D53F52" />
            <rect x="8" y="38" width="10" height="6" rx="3" fill="#D53F52" />

            {/* <!-- Right legs --> */}
            <rect x="46" y="22" width="10" height="6" rx="3" fill="#D53F52" />
            <rect x="46" y="30" width="10" height="6" rx="3" fill="#D53F52" />
            <rect x="46" y="38" width="10" height="6" rx="3" fill="#D53F52" />
            {/* 
                  <!-- Inner white core --> */}
            <rect x="21" y="19" width="23" height="27" rx="14" fill="#FFF" />

            {/* <!-- "=" sign --> */}
            <rect x="26" y="26" width="12" height="4" rx="2" fill="#D53F52" />
            <rect x="26" y="34" width="12" height="4" rx="2" fill="#D53F52" />
          </svg>
        </div>
        <div class={'wog--px-2'}>
          <strong  
            aria-label={cardLabel}
            tabIndex={0}
            role="group"
            class={'wog--text-base wog--font-bold wog--leading-6'}
          >{cardLabel}</strong>
          <br></br>
          <span 
           aria-label={cardDescription}
           tabIndex={0}
           role="group"
           class={'wog--text-xs wog--font-normal'}>{cardDescription}</span>
        </div>
      </div>
    );
  };

  render() {
    const configuration = appStore.get('configuration');
    const rootClass = {
      'wog--tab-view-container': true,
      'wog-feature-form': true,
      'wog--flex': true,
      'wog--flex-col': true,
      'wog--features-form-custom': true,
      'wog--mobile-features-form ': true,
      'wog--position-left-side': configuration.position === WidgetPosition.MiddleLeft,
      'wog--position-right-side': configuration.position === WidgetPosition.MiddleRight,
      'wog--position-bottom-left': configuration.position === WidgetPosition.BottomLeft,
      'wog--position-bottom-right': configuration.position === WidgetPosition.BottomRight,
    };

    const backgroundStyle = {
      '--theme-color': configuration.themeColor,
    };

    const textStyles = {
      '--color': configuration.appearance === Appearance.Light ? '#FFFFFF' : '#333333',
    };

    return (
      <div
        class={rootClass}
        style={backgroundStyle}
        ref={el => (this.feedbackContainer = el as HTMLDivElement)}
        tabIndex={0}
        role="group"
        aria-roledescription="Sentiments widget popup">
        <div class={'wog--feature-form-content'}>
          <div class={'wog--features-form-title'} style={textStyles}>
            <p class={'wog--text-xl wog--font-bold wog--leading-7'}>
              {configuration.welcomeMessage}
            </p>
            <wog-close-button
              customStyle={{ 'wog--feature-form-close-button': true, 'wog--z-10': true }}
              onClose={this.handleCancel}
              customAriaLabel={'Close features form popup'}
            />
          </div>
          <div class={'wog--flex wog--h-full wog--flex-col wog--justify-around wog--features-item'}>
            <wog-rating customStyle={{ 'wog--feature-style': true }} displayCloseButton={false} />
            {this.brt(configuration)}
          </div>
        </div>
      </div>
    );
  }
}
